
import { GraphNode, GraphLink } from "./hooks/useGraphTypes";

// Simple fallback without content type text or icon
function renderSimpleFallback(
  ctx: CanvasRenderingContext2D, 
  x: number, 
  y: number, 
  width: number, 
  height: number,
  cornerRadius: number
) {
  // Just render a clean background with subtle gradient
  const gradient = ctx.createLinearGradient(
    x - width/2, 
    y - height/2, 
    x + width/2, 
    y + height/2
  );
  gradient.addColorStop(0, '#F8FAFC');
  gradient.addColorStop(1, '#F1F5F9');
  
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x - width/2, y - height/2, width, height, cornerRadius);
  ctx.clip();
  ctx.fillStyle = gradient;
  ctx.fillRect(x - width/2, y - height/2, width, height);
  ctx.restore();
}

// Global image cache to prevent reload on every redraw
const imageCache: Map<string, HTMLImageElement> = new Map();
const imageLoadingStatus: Map<string, 'loading' | 'loaded' | 'error'> = new Map();

// Function to load and cache images
function getCachedImage(thumbnailUrl: string): HTMLImageElement | null {
  // Return from cache if already loaded
  if (imageCache.has(thumbnailUrl)) {
    return imageCache.get(thumbnailUrl) || null;
  }
  
  // Don't try to load again if we're already loading or had an error
  if (imageLoadingStatus.get(thumbnailUrl) === 'loading' || 
      imageLoadingStatus.get(thumbnailUrl) === 'error') {
    return null;
  }
  
  // Skip invalid URLs
  if (!thumbnailUrl || thumbnailUrl.trim() === '') {
    imageLoadingStatus.set(thumbnailUrl, 'error');
    return null;
  }
  
  // Start loading the image
  imageLoadingStatus.set(thumbnailUrl, 'loading');
  const img = new Image();
  img.crossOrigin = "Anonymous";
  
  img.onload = () => {
    imageCache.set(thumbnailUrl, img);
    imageLoadingStatus.set(thumbnailUrl, 'loaded');
  };
  
  img.onerror = () => {
    imageLoadingStatus.set(thumbnailUrl, 'error');
    imageCache.delete(thumbnailUrl);
  };
  
  img.src = thumbnailUrl;
  return null; // Return null while loading
}

export const createNodeCanvasObject = (
  hoveredNode: string | null,
  selectedNode: string | null,
  BASE_NODE_WIDTH: number,
  NODE_ASPECT_RATIO: number,
  SELECTED_NODE_SCALE: number,
  HOVERED_NODE_SCALE: number,
  DEFAULT_NODE_SCALE: number,
  NODE_BORDER_WIDTH: number,
  NODE_TEXT_MAX_WIDTH: number
) => {
  return (node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const { x, y, id, thumbnailUrl, contentType, name, color } = node;
    if (!x || !y) return;

    const baseWidth = BASE_NODE_WIDTH;
    const baseHeight = baseWidth / NODE_ASPECT_RATIO;
    
    // Only use hover or default scale now
    const scale = hoveredNode === id ? HOVERED_NODE_SCALE : DEFAULT_NODE_SCALE;
    
    const zoomFactor = Math.max(0.6, Math.min(1, globalScale / 2));
    
    const width = baseWidth * scale * zoomFactor;
    const height = baseHeight * scale * zoomFactor;
    
    // Constants for appearance
    const cornerRadius = 3 * scale * zoomFactor;
    const isHovered = hoveredNode === id;

    // Draw node border
    ctx.beginPath();
    ctx.roundRect(x - width/2 - NODE_BORDER_WIDTH, y - height/2 - NODE_BORDER_WIDTH, 
                width + NODE_BORDER_WIDTH*2, height + NODE_BORDER_WIDTH*2, cornerRadius);
    
    // Simple border color - no more selected/highlighted states
    ctx.fillStyle = color || '#6B7789';
    ctx.fill();
    
    // Draw node background - always with rounded corners
    ctx.beginPath();
    ctx.roundRect(x - width/2, y - height/2, width, height, cornerRadius - 1);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    
    // Draw thumbnail with cached image
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x - width/2, y - height/2, width, height, cornerRadius - 1);
    ctx.clip();
    
    if (thumbnailUrl && thumbnailUrl.trim() !== '') {
      // Get or start loading the image
      const cachedImg = getCachedImage(thumbnailUrl);
      
      if (cachedImg && cachedImg.complete && cachedImg.naturalWidth > 0) {
        // Use the cached image if it's ready
        const imgAspect = cachedImg.naturalWidth / cachedImg.naturalHeight;
        const frameAspect = width / height;
        
        let drawWidth = width;
        let drawHeight = height;
        let offsetX = 0;
        let offsetY = 0;
        
        if (imgAspect > frameAspect) {
          // Image is wider than frame, crop sides
          drawWidth = height * imgAspect;
          offsetX = (width - drawWidth) / 2;
        } else {
          // Image is taller than frame, crop top/bottom
          drawHeight = width / imgAspect;
          offsetY = (height - drawHeight) / 2;
        }
        
        ctx.drawImage(cachedImg, x - width/2 + offsetX, y - height/2 + offsetY, drawWidth, drawHeight);
      } else {
        // Use fallback while loading or if error
        renderSimpleFallback(ctx, x, y, width, height, cornerRadius - 1);
      }
    } else {
      renderSimpleFallback(ctx, x, y, width, height, cornerRadius - 1);
    }
    
    // Add hover state overlay with title (similar to Frame cards)
    if (isHovered) {
      // Draw semi-transparent black gradient from bottom to provide contrast for text
      const gradientHeight = height * 0.5;
      const gradient = ctx.createLinearGradient(
        x, y + height/2 - gradientHeight,
        x, y + height/2
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)'); // Transparent black for text background
      
      ctx.fillStyle = gradient;
      ctx.fillRect(x - width/2, y + height/2 - gradientHeight, width, gradientHeight);
      
      // Draw title text in white
      if (name) {
        const textSize = 2 * scale * zoomFactor; // Changed from 3 to 2 pixels base size
        ctx.font = `${textSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#FFFFFF';
        
        let title = name || '';
        
        if (title.length > 20) {
          let truncated = '';
          for (let i = 0; i < title.length; i++) {
            const testStr = title.substring(0, i) + '...';
            if (ctx.measureText(testStr).width > NODE_TEXT_MAX_WIDTH) {
              break;
            }
            truncated = testStr;
          }
          title = truncated;
        }
        
        ctx.fillText(title, x, y + height/2 - 2);
      }
    }
    
    ctx.restore();
  };
};

export const createLinkCanvasObject = (
  collectionLinkOpacity: number,
  tagLinkOpacity: number
) => {
  return (link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const start = link.source as any;
    const end = link.target as any;
    
    if (!start || !end || !start.x || !start.y || !end.x || !end.y) return;
    
    const linkOpacity = link.type === 'collection' ? collectionLinkOpacity / 100 : tagLinkOpacity / 100;
    if (linkOpacity <= 0) return;
    
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    
    const baseColor = link.color;
    ctx.strokeStyle = baseColor;
    ctx.globalAlpha = linkOpacity;
    
    if (link.type === 'collection') {
      ctx.lineWidth = 0.5 * Math.min(1, globalScale);
      ctx.setLineDash([]);
    } else {
      ctx.lineWidth = 0.25 * Math.min(1, globalScale);
      ctx.setLineDash([2, 2]);
    }
    
    ctx.stroke();
    
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  };
};

// Function to clear the image cache when needed (e.g., when refreshing thumbnails)
export const clearImageCache = () => {
  imageCache.clear();
  imageLoadingStatus.clear();
};
